import { Box, Link as ChakraLink, Flex, Grid, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { playStoreUrl } from '#features/app-install';
import { LanguageSwitcher } from '#features/i18n/components/LanguageSwitcher';
import {
  FOOTER_CONTACT_EMAIL,
  FOOTER_ROUTES,
  FOOTER_SOCIALS,
} from '#features/layout/constants/footerLinks';
import { DiscordIcon, GooglePlayIcon, Link, RedditIcon, XIcon } from '#features/ui';
import { GameTrademarksNotice } from '#features/ui/components/GameTrademarksNotice';
import { IconButton } from '#features/ui/components/IconButton';

const socials = [
  {
    name: 'discord',
    label: 'Discord',
    href: FOOTER_SOCIALS.discord,
    icon: (
      <Flex
        bgColor="fg"
        borderRadius="full"
        color="black"
        boxSize={7}
        css={{ '& svg': { boxSize: 4 } }}
        justifyContent="center"
        alignItems="center"
      >
        <DiscordIcon />
      </Flex>
    ),
  },
  {
    name: 'x',
    label: 'X',
    href: FOOTER_SOCIALS.x,
    icon: <XIcon />,
  },
  {
    name: 'reddit',
    label: 'Reddit',
    href: 'https://www.reddit.com/r/teamplay/s/LFaLN9ZNcK',
    icon: <RedditIcon />,
  },
  {
    name: 'googlePlay',
    label: 'Google Play',
    href: playStoreUrl('landing-footer'),
    icon: (
      <Flex
        bgColor="fg"
        borderRadius="full"
        color="black"
        boxSize={7}
        css={{ '& svg': { boxSize: 4 } }}
        justifyContent="center"
        alignItems="center"
      >
        <GooglePlayIcon />
      </Flex>
    ),
  },
];

export const LandingFooter = () => {
  const { t } = useLingui();
  const socialLabels: Record<string, string> = {
    discord: t({ id: 'footer.social.discord', message: 'Teamplay on Discord' }),
    x: t({ id: 'footer.social.x', message: 'Teamplay on X' }),
    reddit: t({ id: 'footer.social.reddit', message: 'Teamplay on Reddit' }),
    googlePlay: t({ id: 'footer.social.googlePlay', message: 'Teamplay on Google Play' }),
  };

  return (
    <Box
      as="footer"
      borderTop="1px solid"
      borderColor="border.subtle"
      mt={8}
      pt={{ base: 8, md: 12 }}
      pb={{ base: 6, md: 8 }}
    >
      <Grid
        templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
        gap={{ base: 8, md: 6 }}
        mb={10}
      >
        {/* Brand */}
        <Flex direction="column" gap={3}>
          <Text fontWeight="800" fontSize="lg">
            teamplay.gg
          </Text>
          <Text fontSize="sm" color="fg.muted" lineHeight="1.7" maxW="220px">
            {t`v2.footer.tagline`}
          </Text>
          <Flex data-testid="footer-social-links" gap={0} mt={1}>
            {socials.map(({ name, href, icon }) => (
              <IconButton
                key={name}
                aria-label={socialLabels[name]}
                variant="ghost"
                size="lg"
                css={{ '& > svg': { boxSize: 7 } }}
                asChild
              >
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {icon}
                </a>
              </IconButton>
            ))}
          </Flex>
        </Flex>

        {/* Product */}
        <Flex direction="column" gap={2}>
          <Text fontWeight="700" fontSize="sm" mb={1} color="fg.emphasized">
            {t`v2.footer.product`}
          </Text>
          <Link to={FOOTER_ROUTES.blog} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            {t`footer.blog`}
          </Link>
          <Link to={FOOTER_ROUTES.faq} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            FAQ
          </Link>
          <Link
            to={FOOTER_ROUTES.discordBot}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            Discord App
          </Link>
          <ChakraLink
            href={playStoreUrl('landing-footer')}
            target="_blank"
            rel="noopener noreferrer"
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            Google Play
          </ChakraLink>
        </Flex>

        {/* Company */}
        <Flex direction="column" gap={2}>
          <Text fontWeight="700" fontSize="sm" mb={1} color="fg.emphasized">
            {t`v2.footer.company`}
          </Text>
          <Link
            to={FOOTER_ROUTES.joinTheTeam}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`v2.footer.joinTeam`}
          </Link>
          <Link
            to={FOOTER_ROUTES.partnerWithUs}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`v2.footer.partnerWithUs`}
          </Link>
          <ChakraLink
            href={`mailto:${FOOTER_CONTACT_EMAIL}`}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`footer.contactUs`}
          </ChakraLink>
        </Flex>

        {/* Legal + Language */}
        <Flex direction="column" gap={2}>
          <Text fontWeight="700" fontSize="sm" mb={1} color="fg.emphasized">
            {t`v2.footer.legal`}
          </Text>
          <Link to={FOOTER_ROUTES.terms} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            {t`footer.terms`}
          </Link>
          <Link to={FOOTER_ROUTES.privacy} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            {t`footer.privacyPolicy`}
          </Link>
          <ChakraLink
            href="#"
            className="cky-banner-element"
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`footer.cookieSettings`}
          </ChakraLink>
          <Box mt={2}>
            <LanguageSwitcher />
          </Box>
        </Flex>
      </Grid>

      {/* Игровые трейдмарки: универсальная строка + именные Riot/Epic (см. компонент). */}
      <GameTrademarksNotice mb={8} />

      {/* Bottom bar */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        pt={6}
        borderTop="1px solid"
        borderColor="whiteAlpha.50"
      >
        <Text fontSize="xs" color="fg.muted">
          © {new Date().getFullYear()} Teamplay.gg
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {t`v2.footer.madeFor`}
        </Text>
      </Flex>
    </Box>
  );
};
